
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UserSession } from '@/types/dashboard';

interface ActiveSessionsTableProps {
  sessions: UserSession[];
  loading: boolean;
}

export const ActiveSessionsTable: React.FC<ActiveSessionsTableProps> = ({ sessions, loading }) => {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Aktive brukerøkter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded animate-pulse"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Akkurat nå';
    if (diffInMinutes < 60) return `${diffInMinutes}m siden`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}t siden`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d siden`;
  };

  const getBrowserFromUserAgent = (userAgent?: string) => {
    if (!userAgent) return 'Ukjent';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Annen';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Aktive brukerøkter</CardTitle>
        <CardDescription>
          {sessions.length} aktive økter akkurat nå
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {sessions.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Ingen aktive økter funnet
            </p>
          ) : (
            sessions.map((session) => (
              <div key={session.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">
                      Bruker: {session.user_id.slice(0, 8)}...
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {getBrowserFromUserAgent(session.user_agent)}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Startet: {formatTimeAgo(session.session_start)}
                    {session.ip_address && ` • IP: ${session.ip_address}`}
                  </div>
                </div>
                <Badge variant="default" className="bg-green-100 text-green-800">
                  Aktiv
                </Badge>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};
